import React, { useState, useEffect } from "react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { AlertTriangle, XCircle, CheckCircle, RefreshCw } from "lucide-react";
import { CreditManagementService } from "@/services/CreditManagementService";
import { ClientService } from "@/services/ClientService";
import { Client } from "@/types";

interface CreditLimitWarningProps {
  clientId: string | null;
  invoiceAmount: number;
  onCreditCheck?: (canCreate: boolean, reason?: string) => void;
  className?: string;
}

export const CreditLimitWarning: React.FC<CreditLimitWarningProps> = ({
  clientId,
  invoiceAmount,
  onCreditCheck,
  className = "",
}) => {
  const [client, setClient] = useState<Client | null>(null);
  const [creditValidation, setCreditValidation] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (clientId && clientId.trim() !== "" && invoiceAmount > 0) {
      checkCreditLimit();
    } else {
      setClient(null);
      setCreditValidation(null);
      onCreditCheck?.(true);
    }
  }, [clientId, invoiceAmount]);

  const checkCreditLimit = async () => {
    if (!clientId || clientId.trim() === "") return;

    try {
      setLoading(true);
      // Get client details
      const clientData = await ClientService.getClientById(clientId);
      setClient(clientData);

      if (clientData) {
        // Validate credit limit
        const validation = await CreditManagementService.validateCreditLimit(
          clientId,
          invoiceAmount
        );
        setCreditValidation(validation);
        onCreditCheck?.(validation.canCreate, validation.reason);
      }
    } catch (error) {
      console.error("Error checking credit limit:", error);
      onCreditCheck?.(true); // Allow creation if check fails
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-TN", {
      style: "currency",
      currency: "TND",
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const recalculateCredit = async () => {
    if (!clientId || clientId.trim() === "") return;
    try {
      setLoading(true);
      await CreditManagementService.recalculateClientCredit(clientId);
      await checkCreditLimit(); // Refresh the validation
    } catch (error) {
      console.error("Error recalculating credit:", error);
    } finally {
      setLoading(false);
    }
  };

  if (!client || !creditValidation) {
    return null;
  }

  // No credit limit set
  if (client.creditLimit === 0) {
    return (
      <Alert className={className}>
        <CheckCircle className="h-4 w-4" />
        <AlertDescription>
          No credit limit set for {client.name}. Current balance:{" "}
          {formatCurrency(client.currentCreditBalance)}
        </AlertDescription>
      </Alert>
    );
  }

  // Credit limit exceeded
  if (!creditValidation.canCreate) {
    return (
      <Alert variant="destructive" className={className}>
        <XCircle className="h-4 w-4" />
        <AlertDescription>
          <div className="space-y-2">
            <p>
              <strong>Credit Limit Exceeded</strong>
            </p>
            <p>{creditValidation.reason}</p>
            <div className="flex items-center justify-between text-sm">
              <div>
                <p>
                  Current Balance:{" "}
                  {formatCurrency(creditValidation.currentBalance)}
                </p>
                <p>
                  Credit Limit: {formatCurrency(creditValidation.creditLimit)}
                </p>
                <p>
                  New Balance Would Be:{" "}
                  {formatCurrency(creditValidation.newBalance)}
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={recalculateCredit}
                disabled={loading}
              >
                {loading ? (
                  <RefreshCw className="h-3 w-3 animate-spin" />
                ) : (
                  <RefreshCw className="h-3 w-3" />
                )}
                Recalculate
              </Button>
            </div>
          </div>
        </AlertDescription>
      </Alert>
    );
  }

  // Warning for high credit utilization
  const utilization = CreditManagementService.getCreditUtilization(
    creditValidation.newBalance,
    creditValidation.creditLimit
  );

  if (utilization >= 80) {
    return (
      <Alert className={className}>
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription>
          <div className="space-y-2">
            <p>
              <strong>High Credit Utilization Warning</strong>
            </p>
            <p>
              This invoice will bring {client.name}'s credit utilization to{" "}
              {utilization.toFixed(1)}%
            </p>
            <div className="text-sm">
              <p>
                Current Balance:{" "}
                {formatCurrency(creditValidation.currentBalance)}
              </p>
              <p>New Balance: {formatCurrency(creditValidation.newBalance)}</p>
              <p>
                Credit Limit: {formatCurrency(creditValidation.creditLimit)}
              </p>
              <p>
                Remaining Credit:{" "}
                {formatCurrency(
                  creditValidation.creditLimit - creditValidation.newBalance
                )}
              </p>
            </div>
          </div>
        </AlertDescription>
      </Alert>
    );
  }

  // Good standing
  return (
    <Alert className={className}>
      <CheckCircle className="h-4 w-4" />
      <AlertDescription>
        Credit check passed. {client.name} will have{" "}
        {formatCurrency(
          creditValidation.creditLimit - creditValidation.newBalance
        )}{" "}
        remaining credit.
      </AlertDescription>
    </Alert>
  );
};
